/**
 * Budget review — a guarded "propose → you approve → download a corrected copy" loop for an
 * attached spreadsheet (.xlsx / .csv). The AI proposes cell-level changes; nothing is written
 * until the manager ticks the ones they want and hits Apply. The corrected file is a NEW copy —
 * the original attachment is never touched.
 */
import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table2, Loader2, Download, Wand2, ShieldCheck, CheckCircle2 } from "lucide-react";

export function BudgetReviewDialog({ open, onOpenChange, tenantId, attachment }) {
  const [instruction, setInstruction] = useState("");
  const [reviewing, setReviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null); // { notes, edits, filename, ext }
  const [rows, setRows] = useState([]); // [{ ...edit, approved, value }]

  const reset = () => { setInstruction(""); setResult(null); setRows([]); };
  useEffect(() => { if (open) reset(); /* fresh each open */ }, [open, attachment?.id]);

  const review = async () => {
    if (!attachment?.id || reviewing) return;
    setReviewing(true);
    setResult(null); setRows([]);
    try {
      const r = await api.post(`/tenants/${tenantId}/copilot/budget/review`, {
        attachment_id: attachment.id, instruction: instruction.trim(),
      });
      const edits = r.data?.edits || [];
      setResult({ notes: r.data?.notes || "", filename: r.data?.filename, ext: r.data?.ext, count: edits.length });
      setRows(edits.map((e) => ({ ...e, approved: true, value: e.new_value })));
    } catch (e) {
      toast.error(errMsg(e, "Couldn't review that spreadsheet."));
    } finally {
      setReviewing(false);
    }
  };

  const approvedCount = rows.filter((r) => r.approved).length;
  const setAll = (v) => setRows((list) => list.map((r) => ({ ...r, approved: v })));

  const apply = async () => {
    const picked = rows.filter((r) => r.approved);
    if (!picked.length) { toast.error("Tick at least one change to apply."); return; }
    setApplying(true);
    try {
      const res = await api.post(
        `/tenants/${tenantId}/copilot/budget/apply`,
        { attachment_id: attachment.id, edits: picked.map((r) => ({ sheet: r.sheet, cell: r.cell, new_value: r.value })) },
        { responseType: "blob" },
      );
      const fname = res.headers?.["x-corrected-filename"]
        || `${(attachment.filename || "budget").replace(/\.[^.]+$/, "")} (corrected).${result?.ext || "xlsx"}`;
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Corrected file downloaded — ${picked.length} change${picked.length === 1 ? "" : "s"} applied`);
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e, "Couldn't apply those changes."));
    } finally {
      setApplying(false);
    }
  };

  const busy = reviewing || applying;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent className="max-w-2xl" data-testid="budget-review-dialog">
        <DialogHeader className="text-left">
          <DialogTitle className="font-heading text-xl font-extrabold flex items-center gap-2">
            <span className="h-8 w-8 rounded-xl bg-emerald-100 flex items-center justify-center"><Table2 className="h-4 w-4 text-emerald-600" strokeWidth={2} /></span>
            Review &amp; correct budget
          </DialogTitle>
          <DialogDescription>
            <span className="font-semibold text-ink/80">{attachment?.filename}</span> — I'll suggest cell-by-cell fixes.
            You approve the ones you want; I apply them to a fresh copy and leave your original untouched.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <textarea value={instruction} onChange={(e) => setInstruction(e.target.value)} rows={2} disabled={busy} maxLength={2000}
            placeholder="What should I check or fix? e.g. “the reserve line should be 15% of total income” or “fix any totals that don't add up”"
            data-testid="budget-review-instruction"
            className="w-full resize-none rounded-xl border border-sand-200 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-ink disabled:opacity-60" />

          {!result && (
            <div className="flex items-center justify-end">
              <button onClick={review} disabled={busy || !attachment?.id} data-testid="budget-review-run"
                className="rounded-full bg-ink text-white text-sm font-semibold px-5 py-2 hover:bg-ink-700 disabled:opacity-50 flex items-center gap-1.5">
                {reviewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" strokeWidth={2.25} />}
                {reviewing ? "Reviewing…" : "Review this budget"}
              </button>
            </div>
          )}

          {result && (
            <div className="space-y-3" data-testid="budget-review-result">
              {result.notes && (
                <p className="text-sm text-ink/75 leading-relaxed bg-sand-50 border border-sand-200 rounded-xl px-3.5 py-2.5" data-testid="budget-review-notes">
                  {result.notes}
                </p>
              )}

              {rows.length === 0 ? (
                <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-3.5 py-3" data-testid="budget-review-empty">
                  <CheckCircle2 className="h-4 w-4 shrink-0" strokeWidth={2.25} /> No changes needed — nothing looked off for what you asked.
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-semibold text-ink/60 uppercase tracking-wide" data-testid="budget-edit-count">
                      {approvedCount} of {rows.length} change{rows.length === 1 ? "" : "s"} selected
                    </span>
                    <div className="flex gap-3">
                      <button onClick={() => setAll(true)} disabled={busy} data-testid="budget-select-all" className="font-semibold text-ink/60 hover:text-ink">Select all</button>
                      <button onClick={() => setAll(false)} disabled={busy} data-testid="budget-select-none" className="font-semibold text-ink/60 hover:text-ink">None</button>
                    </div>
                  </div>

                  <div className="max-h-[46vh] overflow-y-auto rounded-xl border border-sand-200 divide-y divide-sand-100" data-testid="budget-edit-list">
                    {rows.map((r, i) => (
                      <label key={i} data-testid={`budget-edit-${i}`}
                        className={`flex gap-3 px-3 py-2.5 cursor-pointer transition-colors ${r.approved ? "bg-white" : "bg-sand-50/60"}`}>
                        <input type="checkbox" checked={r.approved} disabled={busy} data-testid={`budget-edit-approve-${i}`}
                          onChange={(e) => setRows((list) => list.map((x, j) => j === i ? { ...x, approved: e.target.checked } : x))}
                          className="mt-1 h-4 w-4 accent-emerald-600 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[11px] font-bold rounded-md bg-ink/5 text-ink/70 px-1.5 py-0.5 tabular-nums">
                              {r.sheet ? `${r.sheet}!` : ""}{r.cell}
                            </span>
                            <span className="text-xs text-ink/45 line-through max-w-[120px] truncate" title={r.old_value}>{r.old_value || "—"}</span>
                            <span className="text-ink/30 text-xs">→</span>
                            <input value={r.value} disabled={busy || !r.approved} data-testid={`budget-edit-value-${i}`}
                              onChange={(e) => setRows((list) => list.map((x, j) => j === i ? { ...x, value: e.target.value } : x))}
                              className="h-7 w-32 rounded-md border border-sand-200 bg-white px-2 text-xs font-semibold text-ink outline-none focus:border-ink disabled:opacity-50" />
                          </div>
                          {r.reason && <p className="text-xs text-ink/55 mt-1 leading-snug">{r.reason}</p>}
                        </div>
                      </label>
                    ))}
                  </div>
                </>
              )}

              <div className="flex items-center gap-1.5 text-[11px] text-ink/45">
                <ShieldCheck className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
                AI-assisted — double-check figures before you use them. Your original file is never changed.
              </div>

              <div className="flex items-center justify-between gap-2 pt-1">
                <button onClick={review} disabled={busy} data-testid="budget-review-again"
                  className="rounded-full border border-sand-200 bg-white text-sm font-semibold px-4 py-2 text-ink/70 hover:bg-sand-50 disabled:opacity-50">
                  Re-review
                </button>
                <button onClick={apply} disabled={busy || approvedCount === 0} data-testid="budget-apply-download"
                  className="rounded-full bg-emerald-600 text-white text-sm font-semibold px-5 py-2 hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1.5">
                  {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" strokeWidth={2.5} />}
                  {applying ? "Applying…" : `Apply ${approvedCount || ""} & download`}
                </button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
